import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Database, Activity, AlertTriangle, CheckCircle2, HardDrive, Network } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import StatPill from './StatPill';

function fmtBytes(bytes) {
    if (!bytes || !Number.isFinite(bytes)) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let i = 0, v = bytes;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}
function fmtRate(bps) {
    if (!bps || !Number.isFinite(bps)) return '0 B/s';
    return `${fmtBytes(bps)}/s`;
}

const STATUS_TONE = {
    HEALTH_OK: 'good',
    HEALTH_WARN: 'warn',
    HEALTH_ERR: 'crit',
};

export default function CephCard({ ceph }) {
    if (!ceph) {
        return (
            <GlassPaper sx={{
                p: { xs: 1.75, sm: 2.25 },
                background: 'linear-gradient(135deg, rgba(100,116,139,0.12), rgba(71,85,105,0.08))',
            }}>
                <SectionHead status={null} />
                <Typography sx={{ fontSize: 12, opacity: 0.7, mt: 1 }}>
                    Ceph is not configured on this cluster.
                </Typography>
            </GlassPaper>
        );
    }
    if (ceph.available === false) {
        return (
            <GlassPaper sx={{
                p: { xs: 1.75, sm: 2.25 },
                background: 'linear-gradient(135deg, rgba(239,68,68,0.12), rgba(100,116,139,0.08))',
            }}>
                <SectionHead status={null} />
                <Typography sx={{ fontSize: 12, opacity: 0.7, mt: 1 }}>
                    Unable to query Ceph: {ceph.error || 'unknown error'}
                </Typography>
            </GlassPaper>
        );
    }

    const tone = STATUS_TONE[ceph.status] || 'muted';
    const { mons, osds, pgs, storage, io, checks = [] } = ceph;
    const byState = pgs?.byState || {};
    const stateEntries = Object.entries(byState).sort((a, b) => b[1] - a[1]);
    const pgTotal = pgs?.total || 0;
    const healthyPg = byState['active+clean'] || 0;

    return (
        <GlassPaper sx={{
            p: { xs: 1.75, sm: 2.25 },
            background: tone === 'good'
                ? 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(59,130,246,0.08))'
                : tone === 'warn'
                    ? 'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(168,85,247,0.08))'
                    : tone === 'crit'
                        ? 'linear-gradient(135deg, rgba(239,68,68,0.16), rgba(168,85,247,0.08))'
                        : 'linear-gradient(135deg, rgba(100,116,139,0.12), rgba(59,130,246,0.08))',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <SectionHead status={ceph.status} tone={tone} />

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 1,
                mt: 1.5,
            }}>
                <Metric
                    icon={<Database size={14} />}
                    label="Monitors"
                    value={`${mons?.quorum ?? 0} / ${mons?.total ?? 0}`}
                    sub="in quorum"
                />
                <Metric
                    icon={<HardDrive size={14} />}
                    label="OSDs"
                    value={`${osds?.up ?? 0} / ${osds?.total ?? 0}`}
                    sub={`${osds?.in ?? 0} in`}
                />
                <Metric
                    icon={<Activity size={14} />}
                    label="PGs"
                    value={`${healthyPg} / ${pgTotal}`}
                    sub="active+clean"
                />
                <Metric
                    icon={<Network size={14} />}
                    label="I/O"
                    value={`${fmtRate(io?.readBps)} r`}
                    sub={`${fmtRate(io?.writeBps)} w`}
                />
            </Box>

            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HardDrive size={14} style={{ opacity: 0.7 }} />
                    <Typography sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                        <b>{fmtBytes(storage?.usedBytes)}</b> used of {fmtBytes(storage?.totalBytes)}
                        {storage?.pct != null && (
                            <Box component="span" sx={{ opacity: 0.65, ml: 0.75 }}>· {storage.pct.toFixed(1)}%</Box>
                        )}
                    </Typography>
                </Box>
                {storage?.pct != null && (
                    <Box sx={{ flex: '1 1 120px', maxWidth: 220, height: 6, borderRadius: 3, bgcolor: 'rgba(148,163,184,0.16)', overflow: 'hidden' }}>
                        <Box sx={{
                            width: `${Math.min(100, storage.pct)}%`,
                            height: '100%',
                            background: storage.pct >= 85
                                ? 'linear-gradient(90deg,#ef4444,#f87171)'
                                : storage.pct >= 70
                                    ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
                                    : 'linear-gradient(90deg,#10b981,#34d399)',
                            transition: 'width 600ms cubic-bezier(.4,0,.2,1)',
                        }} />
                    </Box>
                )}
            </Box>

            {stateEntries.length > 1 && (
                <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {stateEntries.map(([state, count]) => {
                        const isClean = state === 'active+clean';
                        return (
                            <StatPill key={state} tone={isClean ? 'good' : 'warn'} size="sm">
                                {count}× {state}
                            </StatPill>
                        );
                    })}
                </Box>
            )}

            {checks.length > 0 && (
                <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {checks.map((c) => {
                        const sev = (c.severity || '').toUpperCase();
                        const t = sev === 'HEALTH_ERR' ? 'crit' : sev === 'HEALTH_WARN' ? 'warn' : 'info';
                        return (
                            <Tooltip key={c.code} title={c.code} placement="top-start">
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.75,
                                    fontSize: 11.5,
                                    p: 0.75, px: 1,
                                    borderRadius: 1.5,
                                    bgcolor: t === 'crit' ? 'rgba(239,68,68,0.10)' : t === 'warn' ? 'rgba(245,158,11,0.10)' : 'rgba(59,130,246,0.10)',
                                    border: `1px solid ${t === 'crit' ? 'rgba(239,68,68,0.30)' : t === 'warn' ? 'rgba(245,158,11,0.30)' : 'rgba(59,130,246,0.30)'}`,
                                }}>
                                    <AlertTriangle size={12} style={{ color: t === 'crit' ? '#f87171' : t === 'warn' ? '#fbbf24' : '#60a5fa', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 11.5, lineHeight: 1.3 }}>{c.message}</Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            )}
        </GlassPaper>
    );
}

function SectionHead({ status, tone }) {
    const label = status || 'UNAVAILABLE';
    const icon = tone === 'good'
        ? <CheckCircle2 size={11} />
        : tone === 'crit' || tone === 'warn'
            ? <AlertTriangle size={11} />
            : null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(56,189,248,0.22), rgba(99,102,241,0.18))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#7dd3fc',
                }}><Database size={18} /></Box>
                <Box>
                    <Typography sx={{ fontSize: 10.5, letterSpacing: 1.1, opacity: 0.7, textTransform: 'uppercase', fontWeight: 600 }}>
                        Ceph Storage Cluster
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, lineHeight: 1.15 }}>
                        Distributed Storage Health
                    </Typography>
                </Box>
            </Box>
            {status && <StatPill tone={tone || 'muted'} icon={icon}>{label}</StatPill>}
        </Box>
    );
}

function Metric({ icon, label, value, sub }) {
    return (
        <Box sx={{
            p: 1.25,
            borderRadius: 2,
            background: 'rgba(15,23,42,0.35)',
            border: '1px solid rgba(148,163,184,0.14)',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7 }}>
                <Box sx={{ display: 'flex', color: '#94a3b8' }}>{icon}</Box>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600 }}>{label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums', mt: 0.25, lineHeight: 1.1 }}>
                {value}
            </Typography>
            {sub && (
                <Typography sx={{ fontSize: 10.5, opacity: 0.6, mt: 0.25 }}>{sub}</Typography>
            )}
        </Box>
    );
}
